import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useQueries } from '@tanstack/react-query'
import { fetchJson } from './api'
import type { Recording, Segment } from './types'
import { analysisTimeline } from '@vod/core/playback'
import { isRealtimeRecording } from '@vod/core/voice'
import { mediaToWall, wallToMedia } from './time'

interface TranscriptSegment {
  start: number
  end: number
  text: string
}

/** Keep media-time cues on each decoder, including the preloaded clip. Render
 * above the canvas crops, which cannot include native video captions. */
export function Captions({
  videos,
  index,
  recordingId,
  recording,
  segment,
  audioSegments = [],
  apiBase,
  active,
  enabled,
}: {
  videos: RefObject<(HTMLVideoElement | null)[]>
  index: number
  recordingId?: string
  recording?: Recording
  segment?: Segment
  audioSegments?: Segment[]
  apiBase: string
  active: boolean
  enabled: boolean
}) {
  const transcriptSources = useMemo(() => {
    if (!recording || !recordingId) return []
    if (segment && audioSegments.length && isRealtimeRecording(recording)) {
      const overlapping = audioSegments.filter(
        (a) => a.end > segment.start && a.start < segment.end,
      )
      if (overlapping.length) {
        return [
          ...new Map(overlapping.map((a) => [a.recording.id, a.recording])).values(),
        ]
      }
    }
    return [recording]
  }, [recording, recordingId, segment, audioSegments])

  const queries = useQueries({
    queries: transcriptSources.map((source) => ({
      queryKey: [apiBase, 'transcript', source.id],
      enabled: !!source.id,
      staleTime: 30000,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchJson<TranscriptSegment[]>(
          `${apiBase}/api/recordings/${encodeURIComponent(source.id)}/transcript`,
          signal,
        ),
    })),
  })

  const transcriptEntries = useMemo(
    () =>
      transcriptSources.flatMap((source, i) =>
        (queries[i]?.data ?? []).map((item) => ({ source, item })),
      ),
    [transcriptSources, queries.map((q) => q.data).join(',')],
  )

  const track = useRef<TextTrack | null>(null)
  const [text, setText] = useState('')
  useEffect(() => {
    const video = videos.current[index]
    if (!video) return
    const captions = (track.current ??= video.addTextTrack('captions', 'Transcript'))
    captions.mode = 'hidden'
    setText('')
    const maxMediaSeconds = recording ? recording.duration / 1000 : Infinity
    const cues = transcriptEntries
      .flatMap(({ source, item }) => {
        const cueTime = (ms: number) =>
          recording
            ? wallToMedia(recording, mediaToWall(analysisTimeline(source), ms / 1000))
            : ms / 1000
        const start = Math.max(0, cueTime(item.start))
        const end = Math.min(maxMediaSeconds, Math.max(0, cueTime(item.end)))
        if (end <= start) return []
        return [
          new VTTCue(
            start,
            end,
            item.text
              .replaceAll('&', '&amp;')
              .replaceAll('<', '&lt;')
              .replaceAll('>', '&gt;'),
          ),
        ]
      })
      .sort((a, b) => a.startTime - b.startTime || a.endTime - b.endTime)
    cues.forEach((cue) => captions.addCue(cue))
    const update = () =>
      setText(
        Array.from(captions.activeCues ?? [])
          .map((cue) => (cue as VTTCue).getCueAsHTML().textContent ?? '')
          .join('\n'),
      )
    captions.addEventListener('cuechange', update)
    update()
    return () => {
      captions.removeEventListener('cuechange', update)
      cues.forEach((cue) => captions.removeCue(cue))
    }
  }, [videos, index, recordingId, recording, transcriptEntries])
  return active && enabled && text ? (
    <div className="captions">
      <span>{text}</span>
    </div>
  ) : null
}
